import { DEFAULT_SIZE, GALLERY_SIZE, MAX_PAGE_SIZE } from 'src/constants';
import { z } from 'zod';

// Reusable schema helpers

/**
 * Preprocessor to coerce string or array to array.
 * Handles single filter values in URL (e.g., ?filters=foo becomes ['foo'])
 * Also handles edge case where TanStack Router serializes empty arrays as "[]"
 */
export const stringOrArrayToArray = z.preprocess((val) => {
  if (Array.isArray(val)) return val;
  // Handle edge case where empty array was serialized as "[]" string
  if (typeof val === 'string') {
    if (val === '[]' || val === '' || val === '[""]') return [];
    return [val];
  }
  return [];
}, z.array(z.string()));

/**
 * Safely coerce to number with fallback.
 * Handles empty strings, null, undefined, and NaN values.
 */
export const safeNumber = (fallback: number) =>
  z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return fallback;
    const num = Number(val);
    return Number.isNaN(num) ? fallback : num;
  }, z.number());

/**
 * Strict integer coercer for paging params (page, size).
 * Rejects 0/negatives/non-finite/non-integer/out-of-range values by falling back.
 * This prevents stale URLs (?size=0) from poisoning pagination math.
 *
 * Hardening notes:
 * - Booleans are rejected outright. `Number(true) === 1` would otherwise be accepted.
 * - String inputs must be plain decimal digits (no `0x10` hex, `0o17` octal,
 *   `1e3` scientific notation, `24px`, etc.).
 * - Whitespace-trimmed numeric strings (`'  24  '`) are accepted.
 */
export const safePagingInt = (fallback: number, min: number, max: number) =>
  z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return fallback;
    if (typeof val === 'boolean') return fallback;
    if (Array.isArray(val)) return fallback;
    if (typeof val === 'object') return fallback;

    let num: number;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      // Strictly decimal integer notation; rejects hex/octal/scientific/units.
      if (!/^-?\d+$/.test(trimmed)) return fallback;
      num = Number(trimmed);
    } else if (typeof val === 'number') {
      num = val;
    } else {
      return fallback;
    }

    if (!Number.isFinite(num) || !Number.isInteger(num)) return fallback;
    if (num < min || num > max) return fallback;
    return num;
  }, z.number().int());

/**
 * Layout options for the library/gallery views
 */
export const layoutSchema = z.enum(['grid', 'inline', 'twoup', 'gallery_only']);
export type LayoutOption = z.infer<typeof layoutSchema>;

/**
 * Scope options for filtering assets vs galleries
 */
export const scopeSchema = z.enum(['assets', 'galleries', 'all']);
export type ScopeOption = z.infer<typeof scopeSchema>;

/**
 * Common search schema for library/gallery views.
 * This is the source of truth for URL search params.
 *
 * Using .optional().catch() pattern ensures:
 * 1. All params are optional when navigating (no required search params)
 * 2. Defaults are applied during validation
 * 3. Invalid values fall back to defaults instead of throwing
 */
export const baseSearchSchema = z.object({
  page: safePagingInt(1, 1, Number.MAX_SAFE_INTEGER).optional().catch(1),
  size: safePagingInt(DEFAULT_SIZE, 1, MAX_PAGE_SIZE).optional().catch(DEFAULT_SIZE),
  sort: z.string().optional(),
  term: z.string().optional(),
  filters: stringOrArrayToArray.optional().catch([]),
  startdate: z.string().optional(),
  enddate: z.string().optional(),
  transcript: z.coerce.boolean().default(true).catch(true),
  layout: layoutSchema.optional(),
  scope: scopeSchema.optional(),
});

export type BaseSearchParams = z.infer<typeof baseSearchSchema>;

/**
 * Gallery-specific schema with different default size.
 */
export const gallerySearchSchema = baseSearchSchema.extend({
  size: safePagingInt(GALLERY_SIZE, 1, MAX_PAGE_SIZE).optional().catch(GALLERY_SIZE),
});

export type GallerySearchParams = z.infer<typeof gallerySearchSchema>;

/**
 * Convert search params to OpenSearch query string format.
 * This bridges the typed params to the existing OpenSearch query builder.
 */
export const searchParamsToQueryString = (params: BaseSearchParams, galleryId?: string): string => {
  const parts: string[] = [];

  // Provide defaults for optional fields
  const page = params.page ?? 1;
  const size = params.size ?? DEFAULT_SIZE;
  const filters = params.filters ?? [];

  // Pagination
  const start = (page - 1) * size;
  parts.push(`size=${size}`);
  parts.push(`start=${start}`);

  // Sort
  if (params.sort) {
    parts.push(`sort=${params.sort}`);
  }

  // Search term
  if (params.term) {
    parts.push(`term=${encodeURIComponent(params.term)}`);
  }

  // Filters
  if (filters.length > 0) {
    for (const filter of filters) {
      parts.push(`filters=${encodeURIComponent(filter)}`);
    }
  }

  // Date range
  if (params.startdate) {
    parts.push(`startdate=${params.startdate}`);
  }
  if (params.enddate) {
    parts.push(`enddate=${params.enddate}`);
  }

  // Transcript — always send state; default is ON
  parts.push(`transcript=${params.transcript === false ? 'false' : 'true'}`);

  // Gallery context
  if (galleryId) {
    parts.push(`galleryId=${galleryId}`);
  }

  return parts.join('&');
};
